import { createSlice } from '@reduxjs/toolkit';

import type { PortalAdvogadoState } from '../types';
import {
  carregarCalendarioAdvogado,
  carregarConviteAdvogado,
  carregarNotificacoesAdvogado,
  carregarPortalAdvogado,
  carregarProcessoAdvogado,
  concluirCadastroAdvogado,
  enviarProcuracaoAdvogado,
  marcarNotificacoesLidasAdvogado,
} from './actions';

const initialState: PortalAdvogadoState = {
  convite: null,
  conviteStatus: 'idle',
  conviteErroCodigo: null,
  cadastroStatus: 'idle',
  dashboard: null,
  dashboardStatus: 'idle',
  processoAtual: null,
  processoStatus: 'idle',
  procuracaoStatus: 'idle',
  notificacoes: [],
  notificacoesStatus: 'idle',
  marcos: [],
  calendarioStatus: 'idle',
  calendarioError: null,
  error: null,
  procuracaoError: null,
};

const portalAdvogadoSlice = createSlice({
  name: 'portalAdvogado',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(carregarConviteAdvogado.pending, (state) => {
        state.conviteStatus = 'loading';
        state.cadastroStatus = 'idle';
        state.conviteErroCodigo = null;
        state.error = null;
      })
      .addCase(carregarConviteAdvogado.fulfilled, (state, action) => {
        state.conviteStatus = 'succeeded';
        state.convite = action.payload;
      })
      .addCase(carregarConviteAdvogado.rejected, (state, action) => {
        state.conviteStatus = 'failed';
        state.convite = null;
        state.conviteErroCodigo = action.payload?.codigo ?? null;
        state.error = action.payload?.mensagem ?? null;
      })
      .addCase(concluirCadastroAdvogado.pending, (state) => {
        state.cadastroStatus = 'loading';
        state.error = null;
      })
      .addCase(concluirCadastroAdvogado.fulfilled, (state) => {
        state.cadastroStatus = 'succeeded';
      })
      .addCase(concluirCadastroAdvogado.rejected, (state, action) => {
        state.cadastroStatus = 'failed';
        if (action.payload?.codigo === 'CONVITE_ADVOGADO_UTILIZADO') {
          state.convite = null;
          state.conviteErroCodigo = action.payload.codigo;
        }
        state.error = action.payload?.mensagem ?? null;
      })
      .addCase(carregarPortalAdvogado.pending, (state) => {
        state.dashboardStatus = 'loading';
        state.error = null;
      })
      .addCase(carregarPortalAdvogado.fulfilled, (state, action) => {
        state.dashboardStatus = 'succeeded';
        state.dashboard = action.payload;
      })
      .addCase(carregarPortalAdvogado.rejected, (state, action) => {
        state.dashboardStatus = 'failed';
        state.dashboard = null;
        state.error = action.payload ?? null;
      })
      .addCase(carregarProcessoAdvogado.pending, (state) => {
        state.processoStatus = 'loading';
        state.processoAtual = null;
        state.error = null;
      })
      .addCase(carregarProcessoAdvogado.fulfilled, (state, action) => {
        state.processoStatus = 'succeeded';
        state.processoAtual = action.payload;
      })
      .addCase(carregarProcessoAdvogado.rejected, (state, action) => {
        state.processoStatus = 'failed';
        state.error = action.payload ?? null;
      })
      .addCase(enviarProcuracaoAdvogado.pending, (state) => {
        state.procuracaoStatus = 'loading';
        state.procuracaoError = null;
      })
      .addCase(enviarProcuracaoAdvogado.fulfilled, (state, action) => {
        state.procuracaoStatus = 'succeeded';
        const processo = state.dashboard?.processos.find(
          (item) => item.id === action.payload.processoId,
        );
        if (processo) processo.procuracao = action.payload.procuracao;
        if (
          state.processoAtual?.kind === 'blocked' &&
          state.processoAtual.processo.id === action.payload.processoId
        ) {
          state.processoAtual.processo.procuracao = action.payload.procuracao;
        }
      })
      .addCase(enviarProcuracaoAdvogado.rejected, (state, action) => {
        state.procuracaoStatus = 'failed';
        state.procuracaoError = action.payload ?? null;
      })
      .addCase(carregarNotificacoesAdvogado.pending, (state) => {
        state.notificacoesStatus = 'loading';
      })
      .addCase(carregarNotificacoesAdvogado.fulfilled, (state, action) => {
        state.notificacoesStatus = 'succeeded';
        state.notificacoes = action.payload;
      })
      .addCase(carregarNotificacoesAdvogado.rejected, (state) => {
        state.notificacoesStatus = 'failed';
        state.notificacoes = [];
      })
      .addCase(marcarNotificacoesLidasAdvogado.fulfilled, (state) => {
        state.notificacoes = state.notificacoes.map((notificacao) => ({
          ...notificacao,
          lida: true,
        }));
      })
      .addCase(carregarCalendarioAdvogado.pending, (state) => {
        state.calendarioStatus = 'loading';
        state.calendarioError = null;
      })
      .addCase(carregarCalendarioAdvogado.fulfilled, (state, action) => {
        state.calendarioStatus = 'succeeded';
        state.marcos = action.payload;
      })
      .addCase(carregarCalendarioAdvogado.rejected, (state, action) => {
        state.calendarioStatus = 'failed';
        state.marcos = [];
        state.calendarioError = action.payload ?? null;
      });
  },
});

export const portalAdvogadoReducer = portalAdvogadoSlice.reducer;
